import { useState, useMemo, useRef, useEffect } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserPlus,
  Send,
  Check,
  X,
  Clock,
  Building2,
  MapPin,
  Mail,
  Phone,
  MessageSquare,
  Search,
  Bell,
  CheckCircle2,
  Loader2,
  Handshake,
  Star,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useRequireAuth } from "../hooks/useAuth";
import {
  useCollaborations,
  useCreateCollaboration,
  useCancelCollaboration,
  useNotifications,
  useMarkNotificationRead,
  useSuggestedOperators,
} from "../hooks/useNetworking";
import { useFilterOptions } from "../hooks/useOperators";
import { cn } from "../lib/utils";
import { getTypologyConfig, getTypologyLabel } from "../lib/categoryConfig";
import type { Operator, Collaboration, Notification } from "../types/database";

export function Networking() {
  const { isLoading: authLoading } = useRequireAuth();
  const { regions } = useFilterOptions();

  // State
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"discover" | "pending" | "active">("discover");
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(null);
  const [collaborationMessage, setCollaborationMessage] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);

  // Queries
  const { data: collaborations = [], isLoading: collaborationsLoading } = useCollaborations();
  const { data: notifications = [] } = useNotifications();
  const {
    data: operatorsData,
    isLoading: operatorsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSuggestedOperators(selectedRegion || undefined);

  // Flatten paginated operators
  const suggestedOperators = useMemo(() => {
    return operatorsData?.pages.flatMap(page => page.data) || [];
  }, [operatorsData]);

  const totalOperatorsCount = operatorsData?.pages[0]?.totalCount || 0;

  // Mutations
  const createCollaboration = useCreateCollaboration();
  const cancelCollaboration = useCancelCollaboration();
  const markNotificationRead = useMarkNotificationRead();

  // Derived state
  const pendingCollaborations = useMemo(
    () => collaborations.filter((c) => c.status === "pending"),
    [collaborations]
  );
  const activeCollaborations = useMemo(
    () => collaborations.filter((c) => c.status === "accepted"),
    [collaborations]
  );
  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.is_read),
    [notifications]
  );

  // Filter operators
  const filteredOperators = useMemo(() => {
    let filtered = suggestedOperators;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (op) =>
          op.name.toLowerCase().includes(query) ||
          op.city?.toLowerCase().includes(query) ||
          op.typology?.toLowerCase().includes(query)
      );
    }

    // Exclude operators already in collaboration
    const collaboratedIds = new Set(collaborations.map((c) => c.target_operator_id));
    filtered = filtered.filter((op) => !collaboratedIds.has(op.id));

    return filtered;
  }, [suggestedOperators, searchQuery, collaborations]);

  // Handlers
  const handleSendRequest = async () => {
    if (!selectedOperator) return;

    try {
      await createCollaboration.mutateAsync({
        operatorId: selectedOperator.id,
        message: collaborationMessage || undefined,
      });
      setSelectedOperator(null);
      setCollaborationMessage("");
    } catch (error) {
      console.error("Error sending collaboration request:", error);
    }
  };

  const handleCancelRequest = async (id: string) => {
    try {
      await cancelCollaboration.mutateAsync(id);
    } catch (error) {
      console.error("Error cancelling collaboration:", error);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6 text-amber-500" />
              Networking Operatori
            </h1>
            <p className="text-muted-foreground mt-1">
              Connettiti con altri operatori per creare collaborazioni e pacchetti congiunti
            </p>
          </div>

          {/* Notifications Bell */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative"
            >
              <Bell className="h-4 w-4 mr-2" />
              Notifiche
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadNotifications.length}
                </span>
              )}
            </Button>

            {/* Notifications Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl shadow-2xl border border-border bg-card z-50"
                >
                  <div className="p-3 border-b border-border">
                    <h3 className="font-semibold text-sm">Notifiche</h3>
                  </div>
                  {notifications.length > 0 ? (
                    <div className="divide-y divide-border">
                      {notifications.slice(0, 10).map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkRead={() => markNotificationRead.mutate(notification.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-muted-foreground text-sm">
                      Nessuna notifica
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="group cursor-pointer"
            onClick={() => setActiveTab("pending")}
          >
            <Card className="relative overflow-hidden transition-all duration-200 hover:scale-[1.02]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500 transition-all duration-200 group-hover:h-1.5" />
              <CardContent className="p-5 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold tracking-tight">{pendingCollaborations.length}</p>
                    <p className="text-sm text-muted-foreground">Richieste in attesa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="group cursor-pointer"
            onClick={() => setActiveTab("active")}
          >
            <Card className="relative overflow-hidden transition-all duration-200 hover:scale-[1.02]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500 transition-all duration-200 group-hover:h-1.5" />
              <CardContent className="p-5 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Handshake className="h-6 w-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold tracking-tight">{activeCollaborations.length}</p>
                    <p className="text-sm text-muted-foreground">Collaborazioni attive</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="group cursor-pointer"
            onClick={() => setActiveTab("discover")}
          >
            <Card className="relative overflow-hidden transition-all duration-200 hover:scale-[1.02]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-blue-500 transition-all duration-200 group-hover:h-1.5" />
              <CardContent className="p-5 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold tracking-tight">
                      {operatorsLoading ? "..." : (searchQuery ? filteredOperators.length : totalOperatorsCount || filteredOperators.length)}
                    </p>
                    <p className="text-sm text-muted-foreground">Operatori disponibili</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-2"
        >
          {[
            { id: "discover", label: "Scopri Operatori", icon: Search },
            { id: "pending", label: "In Attesa", icon: Clock, count: pendingCollaborations.length },
            { id: "active", label: "Attive", icon: CheckCircle2, count: activeCollaborations.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                activeTab === tab.id
                  ? "bg-amber-500 text-white"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={cn(
                  "px-1.5 py-0.5 text-xs rounded-full",
                  activeTab === tab.id ? "bg-white/20" : "bg-amber-500/20 text-amber-500"
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === "discover" && (
            <motion.div
              key="discover"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {/* Filters */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <div className="flex-1">
                  <Input
                    placeholder="Cerca per nome, città o tipologia..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon={<Search className="h-4 w-4" />}
                  />
                </div>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="h-10 rounded-lg border border-border bg-card px-3 text-sm focus:border-amber-500 focus:outline-none"
                >
                  <option value="">Tutte le regioni</option>
                  {regions.map((region: string) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </motion.div>

              {/* Results count */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-sm text-muted-foreground"
              >
                {operatorsLoading ? (
                  "Caricamento..."
                ) : (
                  <>
                    {filteredOperators.length} operator{filteredOperators.length === 1 ? "e" : "i"}
                    {(searchQuery || selectedRegion) && ` trovat${filteredOperators.length === 1 ? "o" : "i"}`}
                    {searchQuery && ` per "${searchQuery}"`}
                    {totalOperatorsCount > suggestedOperators.length && !searchQuery && (
                      <span className="text-xs ml-1">
                        (caricati {suggestedOperators.length} di {totalOperatorsCount})
                      </span>
                    )}
                  </>
                )}
              </motion.p>

              {/* Virtualized Operators Grid */}
              <VirtualizedOperatorsGrid
                operators={filteredOperators}
                isLoading={operatorsLoading}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                onFetchNextPage={fetchNextPage}
                onSelectOperator={setSelectedOperator}
              />
            </motion.div>
          )}

          {activeTab === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {collaborationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : pendingCollaborations.length > 0 ? (
                <div className="space-y-3">
                  {pendingCollaborations.map((collab) => (
                    <CollaborationCard
                      key={collab.id}
                      collaboration={collab}
                      onCancel={() => handleCancelRequest(collab.id)}
                      isCancelling={cancelCollaboration.isPending}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Clock className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Nessuna richiesta</h3>
                    <p className="text-muted-foreground">
                      Non hai richieste di collaborazione in attesa
                    </p>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === "active" && (
            <motion.div
              key="active"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {collaborationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : activeCollaborations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeCollaborations.map((collab) => (
                    <ActiveCollaborationCard
                      key={collab.id}
                      collaboration={collab}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Handshake className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Nessuna collaborazione</h3>
                    <p className="text-muted-foreground mb-4">
                      Non hai ancora collaborazioni attive con altri operatori
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab("discover")}
                    >
                      Scopri operatori
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Request Modal */}
        <AnimatePresence>
          {selectedOperator && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedOperator(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-border">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <UserPlus className="h-5 w-5 text-amber-500" />
                    Richiesta di Collaborazione
                  </h2>
                </div>

                <div className="p-6 space-y-4">
                  {/* Operator Preview */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{selectedOperator.name}</h3>
                      {selectedOperator.typology && (
                        <Badge variant="secondary" className="mt-1">
                          {getTypologyLabel(selectedOperator.typology)}
                        </Badge>
                      )}
                      {(selectedOperator.city || selectedOperator.region) && (
                        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {[selectedOperator.city, selectedOperator.region].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      Messaggio (opzionale)
                    </label>
                    <textarea
                      value={collaborationMessage}
                      onChange={(e) => setCollaborationMessage(e.target.value)}
                      placeholder="Scrivi un messaggio per presentarti e spiegare il tuo interesse a collaborare..."
                      className="w-full h-32 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="p-6 border-t border-border flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setSelectedOperator(null)}
                  >
                    Annulla
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleSendRequest}
                    disabled={createCollaboration.isPending}
                  >
                    {createCollaboration.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Invia Richiesta
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function OperatorCard({ operator, onSelect }: { operator: Operator; onSelect: () => void }) {
  const config = operator.typology ? getTypologyConfig(operator.typology, operator.macro_category) : null;
  const TypologyIcon = config?.icon || Building2;

  return (
    <Card className="hover:border-amber-500/50 transition-all hover:shadow-lg group">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: config ? `${config.color}40` : "hsl(var(--secondary))" }}
          >
            <TypologyIcon
              className="h-5 w-5"
              style={{ color: config?.color || "hsl(var(--muted-foreground))" }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate group-hover:text-amber-500 transition-colors">
              {operator.name}
            </h3>
            {operator.typology && (
              <p className="text-xs text-muted-foreground">
                {getTypologyLabel(operator.typology)}
              </p>
            )}
          </div>
          {operator.star_rating && operator.star_rating > 0 && (
            <div className="flex items-center gap-0.5">
              {Array.from({ length: Math.min(operator.star_rating, 5) }).map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
              ))}
            </div>
          )}
        </div>

        {(operator.city || operator.region) && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {[operator.city, operator.region].filter(Boolean).join(", ")}
          </p>
        )}

        <div className="flex items-center gap-2 mt-3">
          {operator.email && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Mail className="h-3 w-3" />
              Contatto disponibile
            </span>
          )}
        </div>

        <Button size="sm" className="w-full mt-3" onClick={onSelect}>
          <UserPlus className="h-4 w-4 mr-2" />
          Richiedi Collaborazione
        </Button>
      </CardContent>
    </Card>
  );
}

function CollaborationCard({
  collaboration,
  onCancel,
  isCancelling,
}: {
  collaboration: Collaboration;
  onCancel: () => void;
  isCancelling: boolean;
}) {
  const operator = collaboration.target_operator;

  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-semibold">{operator?.name || "Operatore"}</h3>
              {operator?.city && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {operator.city}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-amber-500/20 text-amber-600">
              In attesa
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isCancelling}
              className="text-muted-foreground hover:text-red-500"
            >
              {isCancelling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        {collaboration.message && (
          <p className="text-sm text-muted-foreground mt-3 p-3 rounded-lg bg-secondary/50">
            "{collaboration.message}"
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-2">
          Inviata il {new Date(collaboration.created_at).toLocaleDateString("it-IT")}
        </p>
      </CardContent>
    </Card>
  );
}

function ActiveCollaborationCard({ collaboration }: { collaboration: Collaboration }) {
  const operator = collaboration.target_operator;

  return (
    <Card className="border-emerald-500/30 bg-emerald-500/5 hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Handshake className="h-6 w-6 text-emerald-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold truncate">{operator?.name || "Operatore"}</h3>
              <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            </div>
            {operator?.typology && (
              <Badge variant="secondary" className="mt-1">
                {getTypologyLabel(operator.typology)}
              </Badge>
            )}
            {(operator?.city || operator?.region) && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {[operator?.city, operator?.region].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          {operator?.email && (
            <a
              href={`mailto:${operator.email}`}
              className="flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-secondary/50 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Email
            </a>
          )}
          {operator?.phone && (
            <a
              href={`tel:${operator.phone}`}
              className="flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-secondary/50 transition-colors"
            >
              <Phone className="h-4 w-4" />
              Chiama
            </a>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          Collaborazione attiva dal {new Date(collaboration.responded_at || collaboration.created_at).toLocaleDateString("it-IT")}
        </p>
      </CardContent>
    </Card>
  );
}

function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: () => void;
}) {
  const getIcon = () => {
    switch (notification.type) {
      case "collaboration_request":
        return <UserPlus className="h-4 w-4 text-amber-500" />;
      case "collaboration_response":
        return <Check className="h-4 w-4 text-emerald-500" />;
      case "package_invite":
        return <Mail className="h-4 w-4 text-blue-500" />;
      case "package_approved":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "seasonality_alert":
        return <Bell className="h-4 w-4 text-purple-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div
      className={cn(
        "p-3 hover:bg-secondary/50 transition-colors cursor-pointer",
        !notification.is_read && "bg-amber-500/5"
      )}
      onClick={onMarkRead}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm", !notification.is_read && "font-medium")}>
            {notification.title}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {new Date(notification.created_at).toLocaleDateString("it-IT", {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
        {!notification.is_read && (
          <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-1.5" />
        )}
      </div>
    </div>
  );
}

// ============================================
// SKELETON COMPONENTS
// ============================================

function OperatorCardSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="h-4 bg-secondary rounded w-3/4" />
            <div className="h-3 bg-secondary rounded w-1/2" />
          </div>
        </div>
        <div className="h-3 bg-secondary rounded w-2/3 mt-3" />
        <div className="h-3 bg-secondary rounded w-1/3 mt-2" />
        <div className="h-8 bg-secondary rounded mt-3" />
      </CardContent>
    </Card>
  );
}

function SkeletonGrid({ count }: { count: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <OperatorCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ============================================
// VIRTUALIZED GRID (Window Scroll)
// ============================================

const ESTIMATED_ROW_HEIGHT = 200; // Estimated height, will be measured dynamically
const COLUMNS = { sm: 1, md: 2, lg: 3 };

function VirtualizedOperatorsGrid({
  operators,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onFetchNextPage,
  onSelectOperator,
}: {
  operators: Operator[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  onFetchNextPage: () => void;
  onSelectOperator: (op: Operator) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(3);

  // Update columns based on window width
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 768) setColumns(COLUMNS.sm);
      else if (width < 1024) setColumns(COLUMNS.md);
      else setColumns(COLUMNS.lg);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // Calculate rows
  const rowCount = Math.ceil(operators.length / columns);

  // Get scroll margin (distance from top of page to container)
  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setScrollMargin(containerRef.current.offsetTop);
    }
  }, []);

  // Use window virtualizer for page scroll with dynamic measurement
  const virtualizer = useWindowVirtualizer({
    count: rowCount + (hasNextPage ? 1 : 0), // +1 for loading row
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 5,
    scrollMargin,
  });

  // Measure elements dynamically
  const measureElement = virtualizer.measureElement;

  // Infinite scroll - load more when approaching bottom (window scroll)
  useEffect(() => {
    const handleScroll = () => {
      if (isFetchingNextPage || !hasNextPage) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrolledPercentage = (scrollTop + windowHeight) / documentHeight;

      // Load more when scrolled past 80%
      if (scrolledPercentage > 0.8) {
        onFetchNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFetchingNextPage, hasNextPage, onFetchNextPage]);

  // Show skeleton grid for initial loading
  if (isLoading) {
    return <SkeletonGrid count={9} />;
  }

  // Empty state
  if (operators.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nessun risultato</h3>
          <p className="text-muted-foreground">
            Nessun operatore trovato con i filtri selezionati
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div ref={containerRef}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const isLoaderRow = virtualRow.index === rowCount;
          const startIndex = virtualRow.index * columns;
          const rowOperators = operators.slice(startIndex, startIndex + columns);

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
              }}
            >
              {isLoaderRow ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                  {isFetchingNextPage && (
                    <>
                      <OperatorCardSkeleton />
                      <OperatorCardSkeleton />
                      <OperatorCardSkeleton />
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                  {rowOperators.map((operator) => (
                    <OperatorCard
                      key={operator.id}
                      operator={operator}
                      onSelect={() => onSelectOperator(operator)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Load more indicator at bottom */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
          <span className="ml-2 text-sm text-muted-foreground">Caricamento...</span>
        </div>
      )}
    </div>
  );
}

